import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
 * Copyright (C) 2025 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import { Alert } from '@instructure/ui-alerts';
import { Button } from '@instructure/ui-buttons';
import { Flex } from '@instructure/ui-flex';
import { Heading } from '@instructure/ui-heading';
import { Text } from '@instructure/ui-text';
import formatMessage from 'format-message';
import { useEffect, useRef, useState } from 'react';
import CanvasSelect from '../shared/CanvasSelect';
import { trackPendoEvent } from '../utils/trackPendoEvent';
const LANGUAGE_ERROR_ID = 'cc-asr-language-error';
export const AutoCaptioning = ({ onCancel, onPrimary, liveRegion, languages, mountNode, onDirtyStateChanged, elementRef, }) => {
    const [selectedLanguageId, setSelectedLanguageId] = useState('');
    const [showLanguageError, setShowLanguageError] = useState(false);
    const languageInputRef = useRef(null);
    useEffect(() => {
        languageInputRef.current?.focus();
    }, []);
    const handleLanguageChange = (_event, languageId) => {
        if (languageId) {
            setSelectedLanguageId(languageId);
            setShowLanguageError(false);
        }
        onDirtyStateChanged?.(Boolean(languageId));
    };
    const handlePrimaryClick = () => {
        if (!selectedLanguageId) {
            trackPendoEvent('canvas_caption_validation_error', {
                flow_type: 'request_auto',
                error_type: 'missing_language',
            });
            setShowLanguageError(true);
            languageInputRef.current?.focus();
            return;
        }
        onPrimary(selectedLanguageId);
    };
    return (_jsxs(Flex, { as: "div", direction: "column", gap: "medium", elementRef: elementRef, tabIndex: -1, children: [_jsxs(Flex.Item, { overflowY: "hidden", overflowX: "hidden", children: [_jsx(Heading, { as: "h4", variant: "titleCardMini", children: formatMessage('Automatic captioning') }), _jsx(Text, { variant: "contentSmall", children: formatMessage('Our technology generates ~85% accurate captions.') })] }), _jsxs(Flex, { gap: "small", direction: "column", children: [_jsx(CanvasSelect, { inputRef: (el) => {
                            languageInputRef.current = el;
                        }, label: formatMessage('Language Spoken in This Media*'), placeholder: formatMessage('Select Language'), value: selectedLanguageId, mountNode: mountNode, translatedStrings: {
                            USE_ARROWS: formatMessage('Use arrow keys to navigate options.'),
                            LIST_COLLAPSED: formatMessage('List collapsed.'),
                            LIST_EXPANDED: formatMessage('List expanded.'),
                            OPTION_SELECTED: '{option} selected.',
                        }, onChange: handleLanguageChange, "aria-invalid": showLanguageError ? 'true' : undefined, "aria-describedby": showLanguageError ? LANGUAGE_ERROR_ID : undefined, messages: showLanguageError
                            ? [
                                {
                                    type: 'newError',
                                    text: formatMessage('Please select a language'),
                                },
                            ]
                            : [], liveRegion: liveRegion, children: languages.map(option => (_jsx(CanvasSelect.Option, { id: option.id, value: option.id, children: option.label }, option.id))) }), showLanguageError && (_jsx("span", { id: LANGUAGE_ERROR_ID, children: _jsx(Alert, { variant: "error", screenReaderOnly: true, isLiveRegionAtomic: true, liveRegion: liveRegion, children: formatMessage('Please select a language') }) }))] }), _jsxs(Flex, { gap: "small", children: [_jsx(Button, { color: "secondary", onClick: onCancel, textAlign: "center", width: "auto", children: formatMessage('Cancel') }), _jsx(Button, { color: "primary", onClick: handlePrimaryClick, textAlign: "center", width: "auto", children: formatMessage('Request') })] })] }));
};
